import {
    Button,
    Divider,
    Form,
    notification,
    Select,
    Space,
    Typography,
} from 'antd';
import { get } from 'lodash';
import { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FaLightbulb } from 'react-icons/fa';
import TagRender from '../../TagRender';
import { COUNTRY_CODES } from '../../../const/countries.constants';
import { PageHeader } from '../../PageHeader';
import { getRegionsApi } from '../../../api/countries.api';
import { updateSmallFleet } from '../../../api/small-fleet.api';
import { useWorkspaceStore } from '../../../store/workspace.store';
import { generateDataToUpdate } from '../utils/account';

const { Title, Text } = Typography;

const CoverageCities: FC = () => {
    const { t } = useTranslation('settings', { keyPrefix: 'SETTINGS' });

    const workspace = useWorkspaceStore((state) => state.data);
    const [cities, setCities] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [form] = Form.useForm();

    const selectCities = (value: string) => {
        if (value !== workspace?.country) form.setFieldValue('coverageCities', []);

        setCities([]);
        notification.destroy();

        getRegionsApi(value)
            .then(({ data }: any) => {
                const citiesSorted = data.cities.sort();
                setCities(citiesSorted);
            })
            .catch((error: any) => {
                const code = get(error, 'response.status', 500);
                const message = code === 404 ? t('GENERAL.COUNTRY_NOT_FOUND') : t('GENERAL.ERROR_OCCURRED');
                notification.error({
                    message: 'Error',
                    description: message,
                });
            })
            .finally(() => setLoading(false));
    };

    const onSubmit = () => {
        setLoading(true);
        form.validateFields()
            .then(async (values) => {
                const dataToUpdate = await generateDataToUpdate({
                    formValues: {
                        coverageCities: values.coverageCities,
                        countryCodeName: COUNTRY_CODES[workspace?.country as keyof typeof COUNTRY_CODES],
                    },
                    activeWorkspace: workspace,
                    defaultLogo: workspace?.image || '',
                    isLogoUploaded: null,
                    setLogoUploaded: () => {},
                });

                const fleetId = workspace?._id || '';

                updateSmallFleet(fleetId, dataToUpdate)
                    .then(() => {
                        notification.success({
                            message: t('GENERAL.DATA_UPDATED_SUCCESS'),
                        });
                    })
                    .catch((error) => {
                        const description = get(error, 'response.data.message', t('GENERAL.DATA_UPDATED_ERROR'));
                        notification.error({
                            message: 'Error',
                            description,
                        });
                    })
                    .finally(() => setLoading(false));
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        form.setFieldsValue({
            coverageCities: workspace?.coverageCities,
        });
        selectCities(workspace?.country || '');
    }, []);

    return (
        <PageHeader
            title={(
                <Title level={5} style={{ marginBottom: 0 }}>
                    {t('GENERAL.COVERAGE_CITIES')}
                </Title>
            )}
            extra={(
                <Button
                    type="primary"
                    size="small"
                    loading={loading}
                    onClick={() => onSubmit()}
                >
                    {t('ACTIONS.SAVE')}
                </Button>
            )}
            footer={(
                <div
                    style={{
                        borderRadius: 8,
                        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.08)',
                        padding: '16px 24px',
                    }}
                >
                    <Space direction="vertical" size={16}>
                        <Form form={form} layout="vertical" style={{ width: '100%' }}>
                            <Form.Item
                                key="coverageCities"
                                name="coverageCities"
                                label={t('GENERAL.SELECT_CITIES')}
                                rules={[{ required: true, message: t('GENERAL.FIELD_REQUIRED') }]}
                            >
                                <Select
                                    mode="multiple"
                                    allowClear
                                    tagRender={TagRender}
                                    placeholder={t('GENERAL.SELECT_CITIES')}
                                >
                                    {cities && cities.map((city: any) => (
                                        <Select.Option
                                            key={city}
                                            value={city}
                                        >
                                            {city}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Form>
                    </Space>

                    <Divider style={{ margin: '16px 0' }} />

                    <Space size={8}>
                        <FaLightbulb size={16} style={{ verticalAlign: 'middle' }} />
                        <Text>
                            <Trans
                                t={t}
                                i18nKey="GENERAL.COVERAGE_CITIES_HELP"
                                components={{ b: <b /> }}
                            />
                        </Text>
                    </Space>
                </div>
            )}
        />

    );
};

export { CoverageCities };
